// UI Components Module
// Reusable modal, toast, and loader components

/**
 * Toast Notification System
 */
export function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function sanitizeAssetUrl(value) {
    const candidate = String(value ?? '').trim();
    if (!candidate) {
        return '';
    }

    if (/[<>"']/.test(candidate)) {
        return '';
    }

    if (/^data:image\/[a-z0-9.+-]+;base64,[a-z0-9+/=\s]+$/i.test(candidate)) {
        return candidate;
    }

    if (/^https?:\/\//i.test(candidate) || candidate.startsWith('/')) {
        return candidate;
    }

    return '';
}

export class Toast {
    constructor() {
        this.container = null;
        this.init();
    }

    init() {
        // Create toast container if it doesn't exist
        if (!document.getElementById('toast-container')) {
            this.container = document.createElement('div');
            this.container.id = 'toast-container';
            this.container.className = 'toast-container';
            document.body.appendChild(this.container);
        } else {
            this.container = document.getElementById('toast-container');
        }
    }

    show(message, type = 'info', duration = 4000) {
        const toast = document.createElement('div');
        toast.className = `toast toast-${type}`;
        const safeMessage = escapeHtml(message);
        
        const icons = {
            success: '✓',
            error: '✕',
            warning: '⚠',
            info: 'ℹ'
        };

        toast.innerHTML = `
            <span class="toast-icon">${icons[type] || icons.info}</span>
            <span class="toast-message">${safeMessage}</span>
            <button class="toast-close">&times;</button>
        `;

        this.container.appendChild(toast);

        // Add click handler for close button
        toast.querySelector('.toast-close').addEventListener('click', () => {
            this.dismiss(toast);
        });

        // Auto-dismiss
        setTimeout(() => {
            this.dismiss(toast);
        }, duration);

        return toast;
    }

    dismiss(toast) {
        toast.classList.add('dismissing');
        setTimeout(() => {
            toast.remove();
        }, 300);
    }

    success(message, duration) {
        return this.show(message, 'success', duration);
    }

    error(message, duration) {
        return this.show(message, 'error', duration);
    }

    warning(message, duration) {
        return this.show(message, 'warning', duration);
    }

    info(message, duration) {
        return this.show(message, 'info', duration);
    }
}

/**
 * Modal Component
 */
export class Modal {
    constructor(options = {}) {
        this.options = {
            closable: true,
            ...options
        };
        this.activeModal = null;
    }

    show(modalId, data = null) {
        const modal = document.getElementById(modalId);
        if (!modal) {
            console.error(`Modal with id "${modalId}" not found`);
            return;
        }

        this.activeModal = modal;
        modal.classList.add('active');

        // Emit show event
        modal.dispatchEvent(new CustomEvent('modal:show', { detail: data }));

        // Setup close handlers
        if (this.options.closable) {
            const closeBtn = modal.querySelector('.modal-close, [data-modal-close]');
            if (closeBtn) {
                closeBtn.addEventListener('click', () => this.hide(modalId));
            }

            const overlay = modal.querySelector('.modal-overlay');
            if (overlay) {
                overlay.addEventListener('click', () => this.hide(modalId));
            }

            // ESC key
            document.addEventListener('keydown', this.handleEscKey);
        }

        // Focus first input
        const firstInput = modal.querySelector('input, button:not(.modal-close)');
        if (firstInput) {
            setTimeout(() => firstInput.focus(), 100);
        }
    }

    hide(modalId) {
        const modal = document.getElementById(modalId);
        if (!modal) return;

        modal.classList.remove('active');
        this.activeModal = null;
        document.removeEventListener('keydown', this.handleEscKey);

        // Emit hide event
        modal.dispatchEvent(new CustomEvent('modal:hide'));
    }

    handleEscKey = (e) => {
        if (e.key === 'Escape' && this.activeModal) {
            this.hide(this.activeModal.id);
        }
    }

    confirm(message, onConfirm, onCancel = null) {
        return new Promise((resolve) => {
            const safeMessage = escapeHtml(message);
            // Create confirmation modal
            const modal = document.createElement('div');
            modal.className = 'modal active';
            modal.innerHTML = `
                <div class="modal-overlay"></div>
                <div class="modal-content confirmation-modal">
                    <div class="modal-body">
                        <p>${safeMessage}</p>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-outline" data-modal-action="cancel">Cancel</button>
                        <button class="btn btn-danger" data-modal-action="confirm">Confirm</button>
                    </div>
                </div>
            `;
            document.body.appendChild(modal);

            const close = () => {
                modal.remove();
                document.removeEventListener('keydown', handleEsc);
            };

            const handleEsc = (e) => {
                if (e.key === 'Escape') {
                    resolve(false);
                    close();
                }
            };

            document.addEventListener('keydown', handleEsc);

            modal.querySelector('[data-modal-action="cancel"]').addEventListener('click', () => {
                if (onCancel) onCancel();
                resolve(false);
                close();
            });

            modal.querySelector('[data-modal-action="confirm"]').addEventListener('click', () => {
                if (onConfirm) onConfirm();
                resolve(true);
                close();
            });

            modal.querySelector('.modal-overlay').addEventListener('click', () => {
                if (onCancel) onCancel();
                resolve(false);
                close();
            });
        });
    }
}

/**
 * Loader Component
 */
export class Loader {
    constructor() {
        this.overlay = null;
    }

    show(message = 'Loading...') {
        if (this.overlay) return;

        this.overlay = document.createElement('div');
        this.overlay.className = 'loader-overlay';
        const safeMessage = escapeHtml(message);
        this.overlay.innerHTML = `
            <div class="loader-content">
                <div class="loader-spinner"></div>
                <p class="loader-message">${safeMessage}</p>
            </div>
        `;
        document.body.appendChild(this.overlay);

        // Prevent body scroll
        document.body.style.overflow = 'hidden';
    }

    hide() {
        if (!this.overlay) return;

        this.overlay.remove();
        this.overlay = null;

        // Restore body scroll
        document.body.style.overflow = '';
    }

    update(message) {
        if (this.overlay) {
            const messageEl = this.overlay.querySelector('.loader-message');
            if (messageEl) {
                messageEl.textContent = message;
            }
        }
    }
}

// Export singleton instances
export const toast = new Toast();
export const modal = new Modal();
export const loader = new Loader();
